import React, { useEffect, useState } from 'react';
import { OC, FONT } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Badge, Icon } from '../components/ui.js';
import { PageHero } from './contentShared.js';
import { CommunityShareModal, ReviewerApplyModal, NewsletterModal } from '../components/communityModals.js';

const STEPS = [
    { icon: 'plus', title: 'Someone spots it', desc: 'A member finds an opportunity and submits the link in seconds.' },
    { icon: 'shieldCheck', title: 'A volunteer reviews', desc: 'Trained reviewers check eligibility, deadline, and authenticity.' },
    { icon: 'send', title: 'The circle gets it', desc: 'It reaches the feed and regional channels — no one misses out.' },
];

function CommunityScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [channels, setChannels] = useState([]);
    const [stats, setStats] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topReviewers, setTopReviewers] = useState([]);
    const [shareOpen, setShareOpen] = useState(false);
    const [reviewerOpen, setReviewerOpen] = useState(false);
    const [newsOpen, setNewsOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [chRes, stRes, chapRes, revRes] = await Promise.all([
                    OCApi.getCommunityChannels(),
                    OCApi.getCommunityStats(),
                    OCApi.getCommunityChapters(),
                    OCApi.getCommunityTopReviewers({ limit: 5, days: 30 }),
                ]);
                setChannels(chRes.channels || []);
                setStats(stRes.stats || []);
                setChapters(chapRes.chapters || []);
                setTopReviewers(revRes.reviewers || []);
            } catch (err) {
                s.showToast('Could not load community data', 'x');
            }
        })();
    }, []);

    const statBand = stats.length ? stats : [
        { value: '184k+', label: 'Community members' },
        { value: '540', label: 'Volunteer reviewers' },
        { value: '14', label: 'University chapters' },
        { value: '143', label: 'Countries reached' },
    ];

    const platformColor = (p) => (p === 'WhatsApp' ? OC.success : p === 'Telegram' ? '#3B82F6' : OC.orange);

    return React.createElement('div', { style: { background: t.bg.page, color: t.text.primary, fontFamily: FONT.body } },
        React.createElement(PageHero, {
            theme: s.theme,
            eyebrow: 'The Circle',
            title: 'It started as friends',
            accent: 'sharing links.',
            sub: 'People across 143 countries pass good opportunities forward. No gatekeeping — just sharing is caring.',
        },
            React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, icon: React.createElement(Icon, { name: 'plus', size: 16 }), onClick: () => setShareOpen(true) }, 'Share an opportunity'),
            React.createElement(Button, { kind: 'secondary', size: 'lg', theme: s.theme, onClick: () => setReviewerOpen(true) }, 'Become a reviewer'),
        ),
        React.createElement('section', { style: { padding: '24px 32px', background: t.bg.card, borderBottom: `1px solid ${t.border.base}` } },
            React.createElement('div', {
                style: { maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 20 }
            },
                statBand.map((st) =>
                    React.createElement('div', { key: st.label, style: { textAlign: 'center' } },
                        React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, color: OC.orange } }, st.value),
                        React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } }, st.label)
                    )
                )
            )
        ),
        React.createElement('section', { style: { padding: '64px 32px', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: OC.orange } }, 'Sharing is caring'),
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 32, margin: '10px 0 32px' } }, 'How one link reaches thousands'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 } },
                STEPS.map((step, i) =>
                    React.createElement('div', {
                        key: step.title,
                        style: { background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 16, padding: 24 }
                    },
                        React.createElement('div', { style: { width: 48, height: 48, borderRadius: 12, background: OC.orangeMuted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 } },
                            React.createElement(Icon, { name: step.icon, size: 22, color: OC.orange })
                        ),
                        React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 18, margin: '0 0 8px' } }, step.title),
                        React.createElement('p', { style: { fontSize: 14.5, color: t.text.secondary, margin: 0, lineHeight: 1.55 } }, step.desc)
                    )
                )
            )
        ),
        React.createElement('section', { id: 'channels', style: { padding: '48px 32px', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, margin: '0 0 8px' } }, 'Regional channels'),
            React.createElement('p', { style: { color: t.text.secondary, margin: '0 0 24px', maxWidth: 560 } }, 'Join WhatsApp, Telegram, or email channels closest to you.'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 } },
                channels.map((c) =>
                    React.createElement('div', {
                        key: c.id,
                        style: { background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 14, padding: 20 }
                    },
                        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 } },
                            React.createElement('div', null,
                                React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 17, margin: 0 } }, c.region),
                                React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 4 } }, c.handle)
                            ),
                            React.createElement(Badge, { color: platformColor(c.platform), theme: s.theme }, c.platform)
                        ),
                        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                            React.createElement('span', { style: { fontSize: 13, color: t.text.secondary } },
                                React.createElement('strong', { style: { color: t.text.primary } }, c.memberCountDisplay || '—'),
                                ' members'
                            ),
                            React.createElement('button', {
                                type: 'button',
                                onClick: () => window.open(c.joinUrl, '_blank', 'noopener,noreferrer'),
                                style: { background: 'none', border: 'none', color: OC.orange, fontWeight: 600, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }
                            }, 'Join ', React.createElement(Icon, { name: 'arrowRight', size: 13 }))
                        )
                    )
                )
            )
        ),
        React.createElement('section', { id: 'chapters', style: { padding: '48px 32px', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, margin: '0 0 8px' } }, 'University chapters'),
            React.createElement('p', { style: { color: t.text.secondary, margin: '0 0 24px', maxWidth: 560 } }, 'Student-led circles on campus sharing internships, scholarships, and deadlines with their cohort.'),
            chapters.length ? React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 } },
                chapters.map((ch) =>
                    React.createElement('div', {
                        key: ch.id,
                        style: { background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 14, padding: 18 }
                    },
                        React.createElement('div', { style: { fontWeight: 700, fontSize: 16 } }, ch.name),
                        ch.universityName ? React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } }, ch.universityName) : null,
                        React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 8 } },
                            [ch.region, ch.memberCountDisplay ? `${ch.memberCountDisplay} members` : null].filter(Boolean).join(' · ')
                        ),
                        ch.contactUrl ? React.createElement('button', {
                            type: 'button',
                            onClick: () => window.open(ch.contactUrl, '_blank', 'noopener,noreferrer'),
                            style: { marginTop: 10, background: 'none', border: 'none', color: OC.orange, fontWeight: 600, cursor: 'pointer', fontSize: 13 }
                        }, 'Get in touch →') : null
                    )
                )
            ) : React.createElement('div', { style: { color: t.text.secondary } }, 'Chapters coming soon.')
        ),
        React.createElement('section', { style: { padding: '48px 32px 80px', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('div', {
                style: {
                    background: s.theme === 'dark' ? t.bg.card : OC.orangeMuted,
                    border: `1px solid ${t.border.base}`, borderRadius: 20, padding: '40px 36px',
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 32, alignItems: 'start',
                }
            },
                React.createElement('div', null,
                    React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 30, margin: 0 } }, 'Give two hours a week. Open a door for someone.'),
                    React.createElement('p', { style: { color: t.text.secondary, lineHeight: 1.55, marginTop: 12 } }, 'Volunteer reviewers keep the feed trustworthy.'),
                    React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' } },
                        React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: () => setReviewerOpen(true) }, 'Apply to review'),
                        React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, onClick: () => setNewsOpen(true) }, 'Newsletter')
                    )
                ),
                React.createElement('div', { style: { background: t.bg.page, border: `1px solid ${t.border.base}`, borderRadius: 14, padding: 20 } },
                    React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 14 } }, 'Top reviewers this month'),
                    topReviewers.length ? topReviewers.map((r, i) =>
                        React.createElement('div', {
                            key: r.userId || i,
                            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < topReviewers.length - 1 ? `1px solid ${t.border.base}` : 'none' }
                        },
                            React.createElement('div', null,
                                React.createElement('div', { style: { fontWeight: 700, fontSize: 14 } }, r.name),
                                React.createElement('div', { style: { fontSize: 11.5, color: t.text.secondary } }, r.chapter)
                            ),
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 18, color: OC.orange } }, r.count)
                        )
                    ) : React.createElement('p', { style: { fontSize: 13.5, color: t.text.secondary, margin: 0, lineHeight: 1.5 } }, 'No reviews logged yet this month. Approve listings in the admin queue to see live stats here.')
                )
            )
        ),
        React.createElement(CommunityShareModal, { open: shareOpen, onClose: () => setShareOpen(false) }),
        React.createElement(ReviewerApplyModal, { open: reviewerOpen, onClose: () => setReviewerOpen(false) }),
        React.createElement(NewsletterModal, { open: newsOpen, onClose: () => setNewsOpen(false) })
    );
}

export { CommunityScreen };
